import { useMemo } from 'react'
import { isValid } from 'date-fns'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

import {
  dateTime as SharedDateTime,
  getInvoiceCustomFeeLines,
  getInvoiceCustomerMailingAddress,
  getInvoiceProcessingFee,
} from '@blocktickets/shared-utils'

import AuthService from '../../utilities/services/auth.service'
import {
  getPaymentMethodLabelForReceipt,
  capitalizeString,
  formatCurrency,
  formatNumber,
  formatTicketGroups,
  formatTicketLocationValue,
} from '../../utilities/helpers'

import logo from '../../assets/logo.svg'

import './invoicePage.scss'

function getOrganizationLogoUrl(image) {
  if (!image) return null

  const normalizeMediaUrl = (rawUrl) => {
    if (!rawUrl) return null

    const url = String(rawUrl).trim()
    if (!url) return null

    if (/^(data:|blob:|https?:)/i.test(url)) return url
    if (url.startsWith('//')) return `https:${url}`

    const normalizedPath = url.startsWith('/') ? url : `/${url}`
    if (!normalizedPath.startsWith('/uploads/')) return normalizedPath

    const apiBase = String(process.env.REACT_APP_API || '').trim()
    if (!apiBase) return normalizedPath

    const publicBase = apiBase.replace(/\/api\/?$/i, '')
    return `${publicBase}${normalizedPath}`
  }

  if (typeof image === 'string') return normalizeMediaUrl(image)

  return normalizeMediaUrl(
    image?.url ||
      image?.formats?.thumbnail?.url ||
      image?.formats?.small?.url ||
      image?.formats?.medium?.url ||
      image?.formats?.large?.url ||
      image?.data?.attributes?.formats?.thumbnail?.url ||
      image?.data?.attributes?.formats?.small?.url ||
      image?.data?.attributes?.formats?.medium?.url ||
      image?.data?.attributes?.formats?.large?.url ||
      image?.data?.attributes?.url ||
      image?.data?.url ||
      image?.attributes?.url ||
      null
  )
}

function getInvoiceUrlsFromSearch(search) {
  const params = new URLSearchParams(search || '')
  const pdf = params.get('pdf')
  const hosted = params.get('hosted')
  return { pdf, hosted }
}

// Use date-fns-tz to show dates in the event's local timezone.
function formatInvoiceDate(dateLike, tz = 'UTC') {
  if (!dateLike) return 'N/A'

  let dateInput = dateLike
  if (typeof dateLike === 'string') {
    // format yyyy-MM-dd
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateLike)
    // format ISO strings
    const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateLike)
    if (isDateOnly) {
      // yyyy-MM-dd is not a real UTC moment
      // Anchor date-only strings at midnight UTC
      // convert to UTC to be midnight in tz
      // treat as wall‑time in tz
      dateInput = SharedDateTime.localToUtc(`${dateLike}T00:00:00`, tz)
    } else if (!hasZone) {
      // Treat zone-less timestamps as UTC
      dateInput = `${dateLike}Z`
    }
  }

  // turn the input into a Date object
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput)

  // formats date in the event timezone using SharedDateTime.formatInTimeZone(...) without changing the underlying moment in time - midnight time
  return isValid(date)
    ? SharedDateTime.formatInTimeZone(dateInput, tz, 'MMMM d, yyyy')
    : 'N/A'
}

// getDate normalizes dates for display.
// - For date-only strings (yyyy-MM-dd/ISO strings), keep them as-is and let formatting apply timezone.
// - For timestamps, return a Date object.
const getDate = (date) => {
  if (!date) return null
  if (date instanceof Date) return date
  if (typeof date === 'string') return date
  return new Date(date)
}

function safeNumber(val, fallback = 0) {
  const n = Number(val)
  return Number.isFinite(n) ? n : fallback
}

function getCustomFeeNames(order) {
  const relation = order?.custom_fees
  if (!Array.isArray(relation)) return []
  return relation
    .map((fee) => String(fee?.name ?? fee?.attributes?.name ?? '').trim())
    .filter(Boolean)
}

function toCents(amount) {
  return Math.round(safeNumber(amount, 0) * 100)
}

function fromCents(cents) {
  return safeNumber(cents, 0) / 100
}

export default function InvoicePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { uuid, orderId, stripeInvoiceId } = useParams()

  const { order, invoice } = location?.state || {}
  const { pdf, hosted } = useMemo(
    () => getInvoiceUrlsFromSearch(location.search),
    [location.search]
  )

  const resolvedInvoice = useMemo(() => {
    if (invoice) return invoice
    const invoices = order?.stripe_invoices || []
    if (!stripeInvoiceId) return invoices?.[0]
    return invoices?.find((i) => i?.stripeInvoiceId === stripeInvoiceId)
  }, [invoice, order, stripeInvoiceId])

  const invoicePdf = resolvedInvoice?.stripeInvoicePdf || pdf || null
  const invoiceHosted = resolvedInvoice?.stripeInvoiceLink || hosted || null
  const openUrl = invoiceHosted || invoicePdf || null

  const authOrgs = AuthService.getOrg() || []
  const orderOrgUuid =
    order?.organizationUUID ||
    order?.organizationUuid ||
    order?.organization?.uuid ||
    order?.event?.organizationUUID ||
    order?.event?.organization?.uuid ||
    order?.package?.organizationUUID ||
    order?.package?.organization?.uuid ||
    order?.flex_pack?.organizationUUID ||
    order?.flex_pack?.organization?.uuid ||
    order?.access_pass_template?.organizationUUID ||
    order?.access_pass_template?.organization?.uuid
  const matchingAuthOrg = authOrgs.find(
    (orgItem) => orgItem?.uuid === orderOrgUuid
  )
  const authOrg = matchingAuthOrg || authOrgs?.[0]
  const orderOrg =
    order?.event?.organization ||
    order?.package?.organization ||
    order?.flex_pack?.organization ||
    order?.access_pass_template?.organization ||
    order?.organization
  const org = orderOrg || authOrg
  const orgAddress = Array.isArray(org?.address) ? org.address[0] : org?.address
  const sellerName = org?.name || 'blocktickets'
  const orgLogo =
    getOrganizationLogoUrl(orderOrg?.image) ||
    getOrganizationLogoUrl(authOrg?.image) ||
    logo

  const tz = SharedDateTime.ensureIanaTimeZone(
    order?.event?.venue?.timezone ||
      order?.package?.venue?.timezone ||
      order?.flex_pack?.venue?.timezone ||
      order?.access_pass_template?.venue?.timezone
  )

  const eventName =
    order?.package?.name ||
    order?.event?.name ||
    order?.flex_pack?.name ||
    order?.access_pass_template?.name ||
    'Invoice'
  const issueDate = formatInvoiceDate(
    getDate(order?.dateOfIssue || order?.processedAt),
    tz
  )
  const dueDate = formatInvoiceDate(
    getDate(order?.dueDate || order?.processedAt),
    tz
  )

  // group tickets by tickets or package_tickets - to make formatTicketGroups work (to avoid repeating seats)
  const tickets = order?.package
    ? order?.package_tickets
    : order?.tickets || order?.originalTickets || []
  const flexPack = order?.flex_pack || null
  const accessPassTemplate = order?.access_pass_template || null
  const ticketLines = useMemo(() => formatTicketGroups(tickets), [tickets])

  let ticketQty = 0
  // if order is a package, use the original tickets (num of tickets for whole package) - tickets change depending on transfers
  if (order?.package) {
    ticketQty = order?.originalTickets?.length || order.tickets?.length || 0
  } else {
    // use the ticket lines (num of tickets for each line)
    ticketQty = ticketLines.reduce(
      (sum, l) => sum + safeNumber(l.ticketCount, 0),
      0
    )
  }
  const accessPassQty = Math.max(
    1,
    safeNumber(order?.accessPassQuantity, 0) ||
      (Array.isArray(order?.access_passes) ? order.access_passes.length : 0) ||
      1
  )

  const total = safeNumber(order?.total, 0)
  const serviceFee = safeNumber(order?.serviceFee, 0)
  // Same customer-facing fee as InvoiceHtml / emailed invoices. A stored
  // estimatedProcessingFee of 0 must not hide a real processingFee or
  // assignment pricing snapshot.
  const processingFee = getInvoiceProcessingFee(order)
  const totalFeeAmount = safeNumber(order?.totalFeeAmount, 0)
  const amountDue = safeNumber(order?.amountDue, 0)
  const amountOwed = amountDue || total
  const totalCents = toCents(total)
  const serviceFeeCents = toCents(serviceFee)
  const processingFeeCents = toCents(processingFee)
  const totalFeeAmountCents = toCents(totalFeeAmount)
  const subtotalCents =
    totalCents - serviceFeeCents - processingFeeCents - totalFeeAmountCents
  const subtotal = fromCents(subtotalCents)
  const isVoucherBoxOfficeOrder =
    Boolean(order?.voucher) &&
    Boolean(
      order?.box_office || order?.boxOffice || order?.source === 'box_office'
    )
  const displayTotal = fromCents(totalCents)
  const displayServiceFee = fromCents(serviceFeeCents)
  const displayAdditionalFee = fromCents(totalFeeAmountCents)
  const displayProcessingFee = fromCents(processingFeeCents)
  const customFeeLines = getInvoiceCustomFeeLines(order)
  const customFeeLineCents = customFeeLines.reduce(
    (sum, line) => sum + toCents(line.amount),
    0
  )
  const displaySubtotal = customFeeLines.length
    ? fromCents(toCents(subtotal) + customFeeLineCents)
    : subtotal
  const additionalFeeNames = customFeeLines.length
    ? []
    : getCustomFeeNames(order)
  const additionalFeeLabel = additionalFeeNames.length
    ? `Additional fee – ${additionalFeeNames.join(', ')}`
    : 'Additional fee'
  const preTax = +(displaySubtotal / (1 + 0.13)).toFixed(2)
  const includedTax = +(displaySubtotal - preTax).toFixed(2)

  const lines = useMemo(() => {
    const result = []
    const flexPackPrice = safeNumber(flexPack?.price, 0)
    const showFlexPackLine = Boolean(flexPack) && !isVoucherBoxOfficeOrder
    const showAccessPassLine = Boolean(accessPassTemplate)

    if (showFlexPackLine) {
      const gameTickets = safeNumber(flexPack?.gameTickets, 1)
      const description =
        gameTickets > 1
          ? `${flexPack?.name || 'Flex Package'} (includes ${gameTickets} game tickets)`
          : flexPack?.name || 'Flex Package'
      const unitPrice = gameTickets > 0 ? subtotal / gameTickets : 0
      result.push({
        description,
        qty: gameTickets,
        unitPrice: unitPrice,
        amount: unitPrice * safeNumber(gameTickets, 0),
      })
    }

    if (showAccessPassLine) {
      const unitPrice = accessPassQty > 0 ? subtotal / accessPassQty : 0
      result.push({
        description: accessPassTemplate?.name || 'Access Pass',
        qty: accessPassQty,
        unitPrice,
        // Use full subtotal so Amount matches Subtotal (avoids qty × unit drift).
        amount: subtotal,
      })
    }

    if (ticketLines.length > 0) {
      const ticketSubtotal = showFlexPackLine
        ? fromCents(toCents(subtotal - flexPackPrice))
        : subtotal
      const ticketUnitPrice = ticketQty > 0 ? ticketSubtotal / ticketQty : 0
      for (const line of ticketLines) {
        const lineQty = order?.package
          ? ticketQty
          : safeNumber(line.ticketCount, 0)
        // Package lines use the full ticket subtotal so Amount matches Subtotal
        // (avoids qty × rounded unit price drifting by a cent).
        const amt = order?.package ? ticketSubtotal : ticketUnitPrice * lineQty
        result.push({
          description: `${eventName} – Sec ${formatTicketLocationValue(line.sectionNumber, line.generalAdmission ? 'GA' : 'N/A')} – Row ${formatTicketLocationValue(line.rowNumber, line.generalAdmission ? 'GA' : 'N/A')} – Seat ${formatTicketLocationValue(line.seats, line.generalAdmission ? 'GA' : 'N/A')}`,
          qty: lineQty,
          unitPrice: ticketUnitPrice,
          amount: amt,
        })
      }
    }

    customFeeLines.forEach((line) => {
      result.push({
        description: line.name,
        qty: 1,
        unitPrice: line.amount,
        amount: line.amount,
      })
    })

    return result
  }, [
    flexPack,
    accessPassTemplate,
    accessPassQty,
    isVoucherBoxOfficeOrder,
    ticketLines,
    ticketQty,
    subtotal,
    eventName,
    order?.package,
    customFeeLines,
  ])

  const mailingAddress = getInvoiceCustomerMailingAddress(order)
  const buyer = order?.users_permissions_user
  const personName =
    order?.firstName && order?.lastName
      ? capitalizeString(`${order.firstName} ${order.lastName}`)
      : buyer?.firstName && buyer?.lastName
        ? capitalizeString(`${buyer.firstName} ${buyer.lastName}`)
        : ''
  const billToName =
    personName ||
    order?.purchasedByOrganizationName ||
    buyer?.organizationName ||
    'N/A'
  const billToEmail = order?.email || buyer?.email || 'N/A'

  return (
    <div className="invoice-page">
      <div className="invoice-page__actions">
        <Stack direction="horizontal" gap={2}>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate(`/event/${uuid}/assign-tickets`)}
          >
            Back
          </Button>
          {openUrl && (
            <Button
              as="a"
              href={openUrl}
              target="_blank"
              rel="noreferrer"
              variant="primary"
              size="sm"
            >
              Open invoice
            </Button>
          )}
        </Stack>
      </div>

      <div className="invoice-doc" role="document" aria-label="Invoice">
        <div className="invoice-doc__top">
          <div className="invoice-doc__brand">
            <img className="invoice-doc__logo" src={orgLogo} alt={sellerName} />
          </div>
          <div className="invoice-doc__title">Invoice</div>
        </div>

        <div className="invoice-doc__header">
          <div className="invoice-doc__billto">
            <div className="invoice-doc__muted invoice-doc__billto-label">
              Bill to
            </div>
            <div className="invoice-doc__billto-name">{billToName}</div>
            {mailingAddress.lines.map((line, index) => (
              <div
                className="invoice-doc__muted invoice-doc__billto-line"
                key={`${line}-${index}`}
              >
                {line}
              </div>
            ))}
            {!mailingAddress.complete && (
              <div className="invoice-doc__billto-missing">
                {mailingAddress.lines.length
                  ? 'Mailing address incomplete'
                  : 'Mailing address unavailable'}
              </div>
            )}
            <div className="invoice-doc__muted">{billToEmail}</div>
          </div>
          <div className="invoice-doc__seller">
            <div className="invoice-doc__seller-name">{sellerName}</div>
            {/* Optional organization fields if present */}
            {(orgAddress?.address || orgAddress?.address_1) && (
              <div className="invoice-doc__muted">
                {orgAddress.address || orgAddress.address_1}
              </div>
            )}
            {orgAddress?.address_2 && (
              <div className="invoice-doc__muted">{orgAddress.address_2}</div>
            )}
            {(orgAddress?.city || orgAddress?.state) && (
              <div className="invoice-doc__muted">
                {[
                  orgAddress.city,
                  orgAddress.state,
                  orgAddress.zip || orgAddress.zipcode,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </div>
            )}
            {orgAddress?.country && (
              <div className="invoice-doc__muted">{orgAddress.country}</div>
            )}
            {org?.phoneNumber && (
              <div className="invoice-doc__muted invoice-doc__seller-phone">
                {org.phoneNumber}
              </div>
            )}
            {org?.email && (
              <div className="invoice-doc__muted">{org.email}</div>
            )}
          </div>
        </div>

        <div className="invoice-doc__meta-wrap">
          <div className="invoice-doc__meta">
            <div className="invoice-doc__meta-row">
              <div className="invoice-doc__meta-label">Invoice number:</div>
              <div className="invoice-doc__meta-value">
                {resolvedInvoice?.stripeInvoiceId ||
                  order?.invoiceUUID ||
                  order?.orderId ||
                  orderId}
              </div>
            </div>
            {order?.paymentStatus && order.paymentStatus !== 'open' && (
              <div className="invoice-doc__meta-row">
                <div className="invoice-doc__meta-label">Payment method:</div>
                <div className="invoice-doc__meta-value">
                  {getPaymentMethodLabelForReceipt(order)}
                </div>
              </div>
            )}
            <div className="invoice-doc__meta-row">
              <div className="invoice-doc__meta-label">Date of issue:</div>
              <div className="invoice-doc__meta-value">{issueDate}</div>
            </div>
            <div className="invoice-doc__meta-row">
              <div className="invoice-doc__meta-label">Date due:</div>
              <div className="invoice-doc__meta-value">{dueDate}</div>
            </div>
          </div>
        </div>

        <div className="invoice-doc__amount-due">
          <div className="invoice-doc__amount-icon" aria-hidden="true">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 2v2M17 2v2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M3.5 9h17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M6 4h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M9 14h6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <div className="invoice-doc__amount-line">
              {formatCurrency(amountDue)} due {dueDate}
            </div>
            <div className="invoice-doc__muted">We appreciate your support</div>
          </div>
        </div>

        <div className="invoice-doc__table">
          <div className="invoice-doc__thead">
            <div>Description</div>
            <div className="invoice-doc__num">Qty</div>
            <div className="invoice-doc__num">Unit price</div>
            <div className="invoice-doc__num">Amount</div>
          </div>
          {lines.length === 0 ? (
            <div className="invoice-doc__trow invoice-doc__trow--empty">
              <div className="invoice-doc__muted">
                No line items found for this order.
              </div>
              <div />
              <div />
              <div />
            </div>
          ) : (
            lines.map((line, idx) => (
              <div className="invoice-doc__trow" key={`${idx}`}>
                <div className="invoice-doc__desc">
                  <div className="invoice-doc__desc-title">
                    {line.description}
                  </div>
                </div>
                <div className="invoice-doc__num">{formatNumber(line.qty)}</div>
                <div className="invoice-doc__num">
                  {formatCurrency(line.unitPrice)}
                </div>
                <div className="invoice-doc__num">
                  {formatCurrency(line.amount)}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="invoice-doc__bottom">
          <div />
          <div className="invoice-doc__totals">
            <div className="invoice-doc__totals-row">
              <div className="invoice-doc__totals-label">
                <div>Subtotal</div>
                {org?.display_tin && (
                  <div className="invoice-doc__totals-subtext">
                    includes {formatCurrency(includedTax)} HST (13%)
                  </div>
                )}
              </div>
              <div className="invoice-doc__totals-value">
                {formatCurrency(displaySubtotal)}
              </div>
            </div>
            <div className="invoice-doc__totals-row">
              <div className="invoice-doc__totals-label">Service charge</div>
              <div className="invoice-doc__totals-value">
                {formatCurrency(displayServiceFee)}
              </div>
            </div>
            {displayAdditionalFee > 0 && customFeeLines.length === 0 && (
              <div className="invoice-doc__totals-row">
                <div className="invoice-doc__totals-label">
                  {additionalFeeLabel}
                </div>
                <div className="invoice-doc__totals-value">
                  {formatCurrency(displayAdditionalFee)}
                </div>
              </div>
            )}
            <div className="invoice-doc__totals-row">
              <div className="invoice-doc__totals-label">Processing fee</div>
              <div className="invoice-doc__totals-value">
                {formatCurrency(displayProcessingFee)}
              </div>
            </div>
            <div className="invoice-doc__totals-row">
              <div className="invoice-doc__totals-label">Total</div>
              <div className="invoice-doc__totals-value">
                {formatCurrency(displayTotal)}
              </div>
            </div>
            <div className="invoice-doc__totals-row invoice-doc__totals-row--strong invoice-doc__totals-row--amount-due">
              <div className="invoice-doc__totals-label">Amount due</div>
              <div className="invoice-doc__totals-value">
                {formatCurrency(fromCents(toCents(amountOwed)))}
              </div>
            </div>
          </div>
        </div>

        <div className="invoice-doc__footer">
          <div className="invoice-doc__footer-note">
            <div className="invoice-doc__muted">
              {org?.taxId ? `HST - ${org.taxId}` : ''}
            </div>
          </div>
          {org?.tiname && org?.tincode && (
            <div className="invoice-doc__footer-note">
              <div className="invoice-doc__muted">
                {org.tiname}: {org.tincode}
              </div>
            </div>
          )}
          <div className="invoice-doc__muted invoice-doc__footer-note">
            Please note our rules and regulations: all sales are final, and we
            do not offer exchanges or refunds.
          </div>
          <div className="invoice-doc__muted invoice-doc__footer-note">
            For inquiries, email us at {org?.email || 'info@blocktickets.net'}.
          </div>
        </div>
      </div>
    </div>
  )
}
